// Added manual product grid card overrides and Buy Now behavior - Lv Jianming - 2026-08-13 19:23
((createController) => {
  const tagName = 'product-grid-manual-controller';
  if (customElements.get(tagName)) return;

  customElements.define(tagName, createController());
})(() => class ProductGridManualController extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.product-grid-manual');
    if (!this.section) return;

    this.customCovers = new Map();
    this.customCoverObservers = new Map();
    this.moneyWithCurrencyFormat = this.dataset.moneyWithCurrencyFormat || '';
    this.currencyCode = this.dataset.currencyCode || '';
    this.handleSectionClick = this.handleSectionClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.section.addEventListener('click', this.handleSectionClick);
    document.addEventListener('click', this.handleDocumentClick);
    this.captureCustomCovers();
    this.section.querySelectorAll('.product__item-js').forEach((card) => {
      this.syncCurrencyPrice(card);
      this.removeHiddenComparePrice(card);
    });
  }

  disconnectedCallback() {
    if (this.section && this.handleSectionClick) {
      this.section.removeEventListener('click', this.handleSectionClick);
    }
    if (this.handleDocumentClick) {
      document.removeEventListener('click', this.handleDocumentClick);
    }
    if (this.customCoverObservers) {
      this.customCoverObservers.forEach((observer) => observer.disconnect());
      this.customCoverObservers.clear();
    }
  }

  handleSectionClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const buyNowButton = target.closest('[data-product-grid-buy-now]');
    if (buyNowButton && this.section.contains(buyNowButton)) {
      event.preventDefault();
      this.checkout(buyNowButton);
      return;
    }

    const swatch = target.closest('.product__color-swatches--js');
    if (!swatch || !this.section.contains(swatch)) return;

    const card = swatch.closest('.product__item-js');
    this.syncSelectedVariant(card);
    this.showSelectedVariantImage(card);
    this.syncCurrencyPrice(card);
    this.removeHiddenComparePrice(card);
    requestAnimationFrame(() => {
      this.syncSelectedVariant(card);
      this.syncCurrencyPrice(card);
      this.removeHiddenComparePrice(card);
    });
  }

  handleDocumentClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.customCovers) return;

    this.customCovers.forEach((state, card) => {
      if (!state.previewActive || card.contains(target)) return;

      this.restoreCustomCover(state);
    });
  }

  captureCustomCovers() {
    this.section.querySelectorAll('[data-product-grid-custom-cover]').forEach((media) => {
      const card = media.closest('.product__item-js');
      const image = media.querySelector('img.product-image.first-image');
      if (!card || !image) return;

      const attributes = new Map();
      ['src', 'srcset', 'sizes', 'data-src', 'data-srcset'].forEach((name) => {
        attributes.set(name, image.getAttribute(name));
      });
      this.customCovers.set(card, {
        image,
        attributes,
        previewActive: false,
      });

      const state = this.customCovers.get(card);
      const observer = new MutationObserver(() => {
        if (state.previewActive) return;

        this.restoreCustomCover(state);
      });
      this.observeCustomCover(state, observer);
      this.customCoverObservers.set(card, observer);
    });
  }

  showSelectedVariantImage(card) {
    if (!card || !this.customCovers) return;

    const state = this.customCovers.get(card);
    const variantRadios = card.querySelector('variant-radios');
    const variant = variantRadios && variantRadios.currentVariant;
    if (!state || !variant) return;

    const variantImage = variant.featured_media?.preview_image?.src;
    if (!variantImage) {
      this.restoreCustomCover(state);
      return;
    }

    state.previewActive = true;
    state.image.removeAttribute('srcset');
    state.image.setAttribute('src', variantImage);
  }

  observeCustomCover(state, observer) {
    if (!state || !state.image || !observer) return;

    observer.observe(state.image, {
      attributes: true,
      attributeFilter: ['src', 'srcset', 'sizes', 'data-src', 'data-srcset'],
    });
  }

  restoreCustomCover(state) {
    if (!state || !state.image || !state.attributes) return;

    const card = state.image.closest('.product__item-js');
    const observer = card && this.customCoverObservers.get(card);
    if (observer) observer.disconnect();

    state.previewActive = false;
    state.attributes.forEach((value, name) => {
      if (value === null) {
        state.image.removeAttribute(name);
      } else {
        state.image.setAttribute(name, value);
      }
    });

    if (observer) this.observeCustomCover(state, observer);
  }

  syncSelectedVariant(card) {
    if (!card) return;

    const variantRadios = card.querySelector('variant-radios');
    const variant = variantRadios && variantRadios.currentVariant;
    if (!variant || !variant.id) return;

    card.querySelectorAll('[data-product-grid-buy-now]').forEach((button) => {
      button.dataset.variantId = variant.id;
      button.disabled = !variant.available;
      if (variant.available) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', 'true');
      }
    });
  }

  formatCurrencyPrice(cents) {
    if (cents === undefined || cents === null || cents === '') return '';
    if (!this.moneyWithCurrencyFormat) return '';
    if (!window.Shopify || typeof window.Shopify.formatMoney !== 'function') return '';

    try {
      return window.Shopify.formatMoney(cents, this.moneyWithCurrencyFormat);
    } catch (error) {
      return '';
    }
  }

  renderCurrencyPrice(element, cents) {
    if (!element) return;

    const formattedPrice = this.formatCurrencyPrice(cents);
    if (!formattedPrice) return;

    const currencyCode = this.currencyCode;
    const currencyCodeIndex = currencyCode ? formattedPrice.indexOf(currencyCode) : -1;
    if (currencyCodeIndex === -1) {
      element.textContent = formattedPrice;
      return;
    }

    element.textContent = '';
    element.append(document.createTextNode(formattedPrice.slice(0, currencyCodeIndex)));

    const currencyCodeElement = document.createElement('span');
    currencyCodeElement.className = 'product-item__currency-code';
    currencyCodeElement.textContent = currencyCode;
    element.append(currencyCodeElement);

    element.append(document.createTextNode(formattedPrice.slice(currencyCodeIndex + currencyCode.length)));
  }

  syncCurrencyPrice(card) {
    if (!card) return;

    const priceRoot = card.querySelector('[data-product-grid-currency-price]');
    if (!priceRoot) return;

    const variantRadios = card.querySelector('variant-radios');
    const variant = variantRadios && variantRadios.currentVariant;
    if (!variant) return;

    const currentPrice = priceRoot.querySelector('.price-regular .price');
    this.renderCurrencyPrice(currentPrice, variant.price);

    const comparePrice = priceRoot.querySelector('.compare-price');
    const hasValidComparePrice = Boolean(
      variant.compare_at_price && variant.compare_at_price > variant.price
    );
    const comparePriceHiddenBySetting = Boolean(
      priceRoot.querySelector('[data-hide-compare-price="true"]')
    );
    const shouldShowComparePrice = hasValidComparePrice && !comparePriceHiddenBySetting;

    if (comparePrice && shouldShowComparePrice) {
      this.renderCurrencyPrice(comparePrice, variant.compare_at_price);
    }
    if (comparePrice) {
      const comparePriceWrapper = comparePrice.closest(
        '[data-product-grid-compare-price-wrapper], .price-regular, .price-sale'
      );
      const visibilityTarget = comparePriceWrapper || comparePrice;

      if (shouldShowComparePrice) {
        visibilityTarget.removeAttribute('hidden');
      } else {
        comparePrice.textContent = '';
        visibilityTarget.setAttribute('hidden', '');
      }
    }

    const currentPriceWrapper = currentPrice && currentPrice.closest('.price-regular');
    if (currentPriceWrapper) {
      currentPriceWrapper.classList.toggle('primary-color', hasValidComparePrice);
    }
    if (currentPrice) {
      currentPrice.classList.toggle('price--special', hasValidComparePrice);
    }

    const unitPrice = priceRoot.querySelector('.unit-price .number');
    if (unitPrice && variant.unit_price) {
      this.renderCurrencyPrice(unitPrice, variant.unit_price);
    }
  }

  removeHiddenComparePrice(card) {
    if (!card) return;

    const price = card.querySelector('[data-hide-compare-price="true"]');
    if (!price) return;

    price.querySelectorAll('.compare-price').forEach((comparePrice) => {
      const wrapper = comparePrice.closest('.price-regular, .price-sale');
      if (wrapper && wrapper !== price) {
        wrapper.remove();
      } else {
        comparePrice.remove();
      }
    });
  }

  checkout(button) {
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/, '');
    const sellingPlan = button.dataset.sellingPlan;
    let checkoutUrl = `${cartUrl}/${encodeURIComponent(variantId)}:1`;
    if (sellingPlan) {
      checkoutUrl += `?selling_plan=${encodeURIComponent(sellingPlan)}`;
    }

    window.location.assign(checkoutUrl);
  }
});
// End added manual product grid card overrides and Buy Now behavior - Lv Jianming - 2026-08-13 19:23
